import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { apiClient } from '@services/api';
import { useSignalR } from '../hooks/useSignalR';
import { TalkingAvatar } from '@components/TalkingAvatar';
import {
  loadKoridorVoice, tapCallPlayback, UNAVAILABLE, END_MESSAGES,
  type KoridorCall, type KoridorCallState, type StartVoiceCallResponse,
} from '../services/koridorVoice';
import './CallDock.scss';

/** One line of the live words: a sentence of the answer as it is spoken, or what the guest is saying. */
interface CallLine { id: number; role: 'user' | 'assistant'; text: string; live?: boolean }

/** Lines kept on the page; past it the oldest half goes in one step, out of sight above the view. */
const MAX_LINES = 40;

interface CallDockProps {
  /** Close the dock (only offered while no call is running). */
  onClose: () => void;
  /** A photo for the AI goes through the chat like any photo; the page owns sending. */
  onSendPhoto: (file: File) => Promise<void>;
  /** The face takes the screen with only the live words; false shrinks it so the chat shows. */
  focus: boolean;
  onFocusChange: (focus: boolean) => void;
}

/**
 * The AI voice call, docked at the top of the messages page. The call's record is the chat itself:
 * both sides of every turn, the menu and order cards, request updates and photos land in the thread
 * below as they happen, and the AI carries the same conversation on from a typed message to a call and
 * back. The dock only shows the face, what is being said right now, and the call controls.
 * Unmounting it (leaving the page) hangs up.
 */
export default function CallDock({ onClose, onSendPhoto, focus, onFocusChange }: CallDockProps) {
  const [callActive, setCallActive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [phase, setPhase] = useState<KoridorCallState | null>(null);
  // The live words, like song lyrics: every sentence stays as it is spoken, the newest lit at the bottom, the
  // earlier ones dimmed and sliding up out of view. Nothing is cleared when an answer ends — the server
  // finishes writing before the audio finishes playing, and clearing then left the sentence being heard blank.
  const [lines, setLines] = useState<CallLine[]>([]);
  const lineIdRef = useRef(0);
  const trimmedRef = useRef(false);
  const lyricsRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const pushLine = useCallback((update: (prev: CallLine[]) => CallLine[]) => {
    setLines(prev => {
      const next = update(prev);
      if (next.length <= MAX_LINES) return next;
      trimmedRef.current = true;
      return next.slice(-MAX_LINES / 2);
    });
  }, []);

  const addSpoken = useCallback((text: string) => {
    if (!text.trim()) return;
    pushLine(prev => [...prev, { id: ++lineIdRef.current, role: 'assistant', text }]);
  }, [pushLine]);

  // The guest's words while they speak replace their own live line; the final transcript settles it.
  const setHeard = useCallback((text: string, final: boolean) => {
    if (!text.trim()) return;
    pushLine(prev => {
      const last = prev[prev.length - 1];
      if (last?.role === 'user' && last.live)
        return [...prev.slice(0, -1), { ...last, text, live: !final }];
      return [...prev, { id: ++lineIdRef.current, role: 'user', text, live: !final }];
    });
  }, [pushLine]);

  // Keep the newest line at the bottom of the view; the track slides (CSS transition) to get there.
  useLayoutEffect(() => {
    const view = lyricsRef.current, track = trackRef.current;
    if (!view || !track) return;
    const last = track.lastElementChild as HTMLElement | null;
    const offset = last ? Math.max(0, last.offsetTop + last.offsetHeight - view.clientHeight) : 0;
    if (trimmedRef.current) {
      // Lines dropped off the top: jump to the new position in one frame, no slide.
      trimmedRef.current = false;
      track.style.transition = 'none';
      track.style.transform = `translateY(${-offset}px)`;
      void track.offsetHeight;
      track.style.transition = '';
      return;
    }
    track.style.transform = `translateY(${-offset}px)`;
  }, [lines, focus]);

  // The view grows and shrinks with the face (an animated height): follow it to the end.
  useEffect(() => {
    const view = lyricsRef.current, track = trackRef.current;
    if (!view || !track || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const last = track.lastElementChild as HTMLElement | null;
      const offset = last ? Math.max(0, last.offsetTop + last.offsetHeight - view.clientHeight) : 0;
      track.style.transform = `translateY(${-offset}px)`;
    });
    ro.observe(view);
    return () => ro.disconnect();
  }, []);
  const [tool, setTool] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [photosEnabled, setPhotosEnabled] = useState(false);
  const [photoAsk, setPhotoAsk] = useState<string | null>(null);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const { on } = useSignalR();

  const callRef = useRef<KoridorCall | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const avatarMountRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<TalkingAvatar | null>(null);
  const audioTappedRef = useRef(false);

  useEffect(() => {
    if (!avatarMountRef.current) return;
    const coat = getComputedStyle(document.documentElement).getPropertyValue('--theme-color-1').trim();
    const avatar = new TalkingAvatar(avatarMountRef.current, { colors: coat ? { coat } : {} });
    avatar.setMood('smile');
    avatarRef.current = avatar;
    return () => { avatar.destroy(); avatarRef.current = null; };
  }, []);

  useEffect(() => { loadKoridorVoice().catch(() => { /* retried on tap */ }); }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopTimer();
    callRef.current?.hangup().catch(() => { /* page is gone */ });
    callRef.current = null;
  }, [stopTimer]);

  const finishCall = useCallback(() => {
    callRef.current = null;
    stopTimer();
    setCallActive(false);
    setPhase(null);
    setLines([]);
    setTool(null);
    setMuted(false);
    setPhotoAsk(null);
    photoMuteRef.current = false;
    avatarRef.current?.stop();
    avatarRef.current?.setMood('smile');
    audioTappedRef.current = false;
    onFocusChange(false);
  }, [stopTimer, onFocusChange]);

  useEffect(() => on('PhotoRequested', (data) => {
    if (!callRef.current) return;
    const reason = typeof data.reason === 'string' ? data.reason : '';
    setPhotoAsk(reason || 'something');
  }), [on]);

  // The microphone is off while the guest takes and sends a photo: the camera screen, the phone being
  // handled and the silence meanwhile otherwise reach speech-to-text, which turns noise into sentences the
  // guest never said ("I'd like to book a room"). The guest's own mute choice is kept.
  const photoMuteRef = useRef(false);
  const mutedRef = useRef(false);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const releasePhotoMute = useCallback(() => {
    if (!photoMuteRef.current) return;
    photoMuteRef.current = false;
    if (!mutedRef.current) callRef.current?.setMuted(false);
  }, []);

  const openCamera = useCallback(() => {
    if (callRef.current && !photoMuteRef.current) {
      photoMuteRef.current = true;
      callRef.current.setMuted(true);
    }
    photoInputRef.current?.click();
  }, []);

  // The picker closed without a photo: the `cancel` event where the browser has it, else the page getting
  // focus back with nothing picked.
  useEffect(() => {
    const input = photoInputRef.current;
    const onCancel = () => releasePhotoMute();
    const onFocus = () => setTimeout(() => {
      if (photoMuteRef.current && !input?.files?.length && !sendingRef.current) releasePhotoMute();
    }, 1500);
    input?.addEventListener('cancel', onCancel);
    window.addEventListener('focus', onFocus);
    return () => {
      input?.removeEventListener('cancel', onCancel);
      window.removeEventListener('focus', onFocus);
    };
  }, [releasePhotoMute]);

  const sendingRef = useRef(false);
  const onPhotoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) { releasePhotoMute(); return; }
    sendingRef.current = true;
    setSendingPhoto(true);
    setPhotoAsk(null);
    try {
      await onSendPhoto(file);
    } catch (err) {
      console.error('Photo during call failed:', err);
      setError("The photo couldn't be sent. Please try again.");
    } finally {
      sendingRef.current = false;
      setSendingPhoto(false);
      // The photo is with the assistant now; it answers out loud, and the guest can talk back.
      releasePhotoMute();
    }
  };

  // Must run inside the tap: browsers (iOS Safari strictly) only start the microphone and audio from
  // a user gesture.
  const startCall = useCallback(async () => {
    if (starting || callRef.current) return;
    setStarting(true);
    setError(null);
    setLines([]);
    setCallDuration(0);

    try {
      const [{ data }, KoridorVoice] = await Promise.all([
        apiClient.post<StartVoiceCallResponse>('/api/v1/guest/messages/voice-call/start', { voice: 'nova' }),
        loadKoridorVoice(),
      ]);

      if (!data.success || !data.ticket || !data.socketUrl) {
        setError(data.error || UNAVAILABLE);
        return;
      }
      setPhotosEnabled(!!data.photosEnabled);

      const call = await KoridorVoice.start({
        ticket: data.ticket,
        socketUrl: data.socketUrl,
        onState: (state) => {
          setPhase(state);
          if (state !== 'thinking') setTool(null);
          const avatar = avatarRef.current;
          if (!avatar) return;
          if (state === 'thinking') avatar.setMood('thinking');
          else if (state === 'speaking') avatar.setMood('neutral');
          else {
            avatar.setSpeaking(false);
            avatar.setMood('smile');
          }
        },
        onPartial: (text) => setHeard(text, false),
        onUserText: (text) => setHeard(text, true),
        onAssistantText: (text) => {
          if (audioTappedRef.current) avatarRef.current?.cueSentence(text);
          else avatarRef.current?.speakAlong(text);
          addSpoken(text);
        },
        onTool: (name) => setTool(name),
        onError: (message) => setError(message),
        onEnded: (reason) => {
          finishCall();
          const message = END_MESSAGES[reason];
          if (message) setError(message);
        },
      });

      callRef.current = call;
      audioTappedRef.current = !!avatarRef.current && tapCallPlayback(call, avatarRef.current);
      setCallActive(true);
      // A call opens on the face; the guest switches to the chat when they want to read or type.
      onFocusChange(true);
      timerRef.current = setInterval(() => setCallDuration(s => s + 1), 1000);
    } catch (err) {
      console.error('Voice call failed to start:', err);
      setError(UNAVAILABLE);
    } finally {
      setStarting(false);
    }
  }, [starting, finishCall, onFocusChange, setHeard, addSpoken]);

  const endCall = useCallback(async () => {
    const call = callRef.current;
    if (!call) return;
    stopTimer();
    setPhase('ended');
    try {
      // Resolves once Koridor has delivered the closing transcript (up to ~25s).
      await call.hangup();
    } catch (err) {
      console.error('Hangup failed:', err);
    } finally {
      finishCall();
    }
  }, [finishCall, stopTimer]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    if (!photoMuteRef.current) callRef.current?.setMuted(next);
    setMuted(next);
  }, [muted]);

  const formatDuration = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

  const ending = callActive && phase === 'ended';
  const big = callActive && focus;

  const status = starting || phase === 'connecting' ? 'Connecting…'
    : ending ? 'Ending call…'
    : sendingPhoto ? 'Sending photo… (microphone off)'
    : phase === 'thinking' ? (tool ? 'Checking…' : 'Thinking…')
    : phase === 'speaking' ? 'Speaking…'
    : phase === 'listening' ? (muted ? 'Muted' : 'Listening…')
    : 'Talk to our assistant — you can speak over it at any time.';

  return (
    <div className={`call-dock${callActive ? ' call-dock-live' : ''}${big ? ' call-dock-focus' : ''}`}>
      {!callActive && !starting && (
        <button className="call-dock-close" onClick={onClose} aria-label="Close">✕</button>
      )}

      {/* The face centred at the top; what is being said right under it. */}
      <div className={`call-dock-face${phase === 'speaking' ? ' call-dock-face-speaking' : ''}`}
        onClick={callActive ? () => onFocusChange(!focus) : undefined}
        role={callActive ? 'button' : undefined}
        aria-label={callActive ? (focus ? 'Show the chat' : 'Show the assistant') : undefined}>
        <div ref={avatarMountRef} className="call-dock-face-mount" />
      </div>

      <div className="call-dock-body">
        <div className="call-dock-title">
          <span>🎙️ Voice assistant</span>
          {callActive && <span className="call-dock-duration">{formatDuration(callDuration)}</span>}
        </div>
        <div className={`call-dock-status call-dock-status-${phase ?? 'idle'}`}>{status}</div>
        {callActive && (
          <div className="call-dock-lyrics" ref={lyricsRef} aria-live="polite">
            <div className="call-dock-lyrics-track" ref={trackRef}>
              {lines.map((line, i) => (
                <div key={line.id}
                  className={`call-dock-line call-dock-line-${line.role}${i === lines.length - 1 ? ' call-dock-line-current' : ''}`}
                  dir="auto">
                  {line.text}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="call-dock-error">
          {error}
          <button onClick={() => setError(null)} aria-label="Dismiss">✕</button>
        </div>
      )}

      {callActive && photosEnabled && photoAsk && !sendingPhoto && (
        <div className="call-dock-photo-ask">
          <span>📷 The assistant would like to see {photoAsk}.</span>
          <button onClick={openCamera}>Take photo</button>
          <button className="call-dock-photo-ask-dismiss" onClick={() => setPhotoAsk(null)} aria-label="Dismiss">✕</button>
        </div>
      )}
      <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhotoPicked} />

      <div className="call-dock-controls">
        {!callActive ? (
          <button className="call-dock-start" onClick={startCall} disabled={starting}>
            📞 {starting ? 'Connecting…' : 'Start call'}
          </button>
        ) : (
          <>
            <button className={`call-dock-btn${muted ? ' call-dock-btn-on' : ''}`} onClick={toggleMute}
              disabled={ending} aria-pressed={muted}>
              {muted ? '🔇 Unmute' : '🎤 Mute'}
            </button>
            {photosEnabled && (
              <button className="call-dock-btn" onClick={openCamera} disabled={ending || sendingPhoto}>
                📷 Show
              </button>
            )}
            <button className="call-dock-btn call-dock-view" onClick={() => onFocusChange(!focus)}
              aria-pressed={!focus}>
              {focus ? '💬 Chat' : '🙂 Face'}
            </button>
            <button className="call-dock-end" onClick={endCall} disabled={ending}>End call</button>
          </>
        )}
      </div>
    </div>
  );
}
